import {
  PrimaryNavigationStrip,
  BalancedPrimaryNavigationStripButton,
  PrimaryNavigationStripLabel,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-navigation-strip";
import {
  PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX,
  PrimaryNavigationStripFadeInsetContext,
  PrimaryNavigationStripFadeReachProvider,
  getPrimaryNavigationBarBottomClearance,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { ChromeReadyMarker } from "@-/dev.improve.haniverse.components.ui.chrome-ready-marker";
import {
  ActivePrimaryNavigationDestinationNameContext,
  ActivePrimaryNavigationDestinationNameSharedValueContext,
  CommittedPrimaryNavigationDestinationNameRefContext,
  createPressedPrimaryNavigationDestinationStore,
  NavContext,
  PendingPrimaryNavigationDestinationNameContext,
  PressedPrimaryNavigationDestinationStoreContext,
  useIsPrimaryNavigationStripHidden,
} from "@-/dev.improve.haniverse.routes.nav._context";
import { PrimaryControlsStripTitleContext } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.title";
import type { BottomTabBarProps } from "@-/com.npmjs.expo-router/bottom-tabs";
import { useTheme } from "@-/com.npmjs.expo-router/react-navigation";
import { dismissToSynchronously, Tabs, usePathname } from "@-/com.npmjs.expo-router";
import { NavigationBar } from "@-/com.npmjs.expo-navigation-bar";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Platform, useWindowDimensions } from "@-/com.npmjs.react-native";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { ReactNode, Ref } from "react";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { getFocusedRouteName } from "@-/dev.improve.haniverse.routes.nav._lib.focused-route";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";

import type { PrimaryNavigationLayoutScreen } from "@-/dev.improve.haniverse.routes.nav.entities";
import type { RoutePath } from "@-/com.npmjs.expo-router";
import path from "@-/com.npmjs.pathe";
import { useCSSVariable, useUniwind } from "@-/com.npmjs.uniwind";
import {
  createOptimisticActiveTabStore,
  type OptimisticActiveTabStore,
} from "#optimistic-active-primary-navigation-store";
import { PrimaryNavigationCustomizationProvider } from "@-/dev.improve.haniverse.routes.nav._lib.primary-navigation-customization";
import {
  createPrimaryNavigationSceneRemountStore,
  type PrimaryNavigationSceneRemountStore,
} from "#primary-navigation-scene-remount-store";
const hiddenOptions = {
  // SDK 58 treats href:null as an unreachable route and redirects it to Learn.
  // Drawer destinations remain registered; only their tab controls are hidden.
  // oxlint-disable-next-line haniverse/no-return-null -- React Navigation tab-button renderer contract
  tabBarButton: () => null,
  tabBarItemStyle: { display: "none" },
} as const;

function NavTabsChromeReady() {
  // Keep startup's pathname subscription out of the layout. A route change must not
  // recreate every tab's options/listeners just to update this invisible marker.
  const pathname = usePathname();
  return pathname === "/learn" || pathname === "/learn/summary" ? null : <ChromeReadyMarker />;
}

/**
 * Structural shape of the tab navigator's state as read via `navigation.getState()` in the
 * per-screen tabPress listener — kept minimal so the listener stays decoupled from
 * react-navigation's full `TabNavigationState` generics.
 */
type TabsState = { index: number; routes: { name: string; state?: NestedNavigatorState }[] };

/** A nested navigator state as stored on its parent route. Partial states may omit `index`. */
type NestedNavigatorState = {
  index?: number;
  routes: { name?: string; state?: NestedNavigatorState }[];
};

/**
 * Whether a tab's nested navigators all sit on their first route, i.e. the user is already on the
 * tab's base page. A route with no nested state yet has never navigated inside the tab.
 */
function isOnBaseRoute(
  state: NestedNavigatorState | undefined,
  targetSegments: readonly string[],
): boolean {
  if (state === undefined) {
    return true;
  }
  const index = state.index ?? state.routes.length - 1;
  const route = state.routes[0];
  // A single-entry stack can start on a deep link (Learn's deck), not its home page.
  // Remount only when the first route actually matches the requested home destination.
  const [target, ...remaining] = targetSegments;
  return (
    index === 0 &&
    (target === undefined || route?.name === target) &&
    isOnBaseRoute(route?.state, remaining)
  );
}

/**
 * Keys a tab's scene on its remount generation, so a re-tap on the base page remounts it in place
 * (no navigator action, so no stack transition). See `PrimaryNavigationSceneRemountStore`.
 */
function PrimaryNavigationSceneRemountBoundary({
  children,
  name,
  sceneRemountStore,
}: {
  children: ReactNode;
  name: string;
  sceneRemountStore: PrimaryNavigationSceneRemountStore;
}) {
  const generation = useSyncExternalStore(
    sceneRemountStore.subscribe,
    () => sceneRemountStore.getGeneration(name),
    () => sceneRemountStore.getGeneration(name),
  );
  return <Fragment key={generation}>{children}</Fragment>;
}

/** Theme colors from `useCSSVariable` may be HSL channels or resolved hex/rgb/hsl strings. */
function hslFromChannels(channels: string | number | undefined): string | undefined {
  if (channels === undefined || channels === "") {
    return undefined;
  }

  const value = String(channels);
  if (value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")) {
    return value;
  }

  return `hsl(${value})`;
}

/**
 * Wraps `PrimaryNavigationStrip` and applies an optimistic active-tab override so the indicator and
 * active-tint flip on the same frame as a tab tap, even when the destination scene takes
 * time to render. Clears the override once the real navigator state settles on (or away
 * from) the optimistic target.
 */
function OptimisticPrimaryNavigationStrip({
  androidUsesButtonNavBar,
  bottomClearance,
  destinationOrder,
  onCommittedFocusedName,
  optimisticActiveTabStore,
  ...props
}: BottomTabBarProps & {
  androidUsesButtonNavBar: boolean;
  bottomClearance: number;
  destinationOrder: readonly string[] | undefined;
  onCommittedFocusedName: (name: string | null) => void;
  optimisticActiveTabStore: OptimisticActiveTabStore;
}) {
  const optimisticActiveName = useSyncExternalStore(
    optimisticActiveTabStore.subscribe,
    optimisticActiveTabStore.getSnapshot,
    optimisticActiveTabStore.getSnapshot,
  );
  const realIndex = props.state.index;

  const focusedTabName = getFocusedRouteName(props.state);
  // Mirror the COMMITTED focused tab back to the parent so the tabPress listener can tell a
  // re-tap of the active tab from a switch without consulting the racy `navigation.isFocused()`.
  // Synchronous (not in an effect) so the ref is already up to date if a tap fires this frame.
  onCommittedFocusedName(focusedTabName);

  const optimisticIndex = useMemo(() => {
    if (optimisticActiveName === null) {
      return -1;
    }
    return props.state.routes.findIndex((r) => r.name === optimisticActiveName);
  }, [optimisticActiveName, props.state.routes]);

  // Capture the realIndex at the moment the optimistic override was set, so we can detect
  // when the navigator has moved at all (to the target OR somewhere else like a drawer route).
  // Any movement is enough to drop the override.
  const indexAtSetRef = useRef<number | null>(null);
  useEffect(() => {
    if (optimisticActiveName === null) {
      indexAtSetRef.current = null;
      return;
    }
    if (indexAtSetRef.current === null) {
      indexAtSetRef.current = realIndex;
    }
  }, [optimisticActiveName, realIndex]);

  useEffect(() => {
    if (optimisticActiveName === null) {
      return;
    }
    // Converged: the navigator committed the optimistic target (or the tap was a re-tap of the
    // already-focused tab, where they're equal from the start). Clearing here — not only on
    // movement — means a stale override can never outlive the state it predicted: without this,
    // an override whose target the navigator was ALREADY on (so `realIndex` never moves) would
    // stick forever and keep painting a wrong active tab after any later non-tap navigation.
    if (optimisticIndex === realIndex) {
      // The bar already paints this tab, so dropping the override needs no render. No render
      // also means the `null` branch of the capture effect above never runs, so forget the
      // captured index here or the NEXT tap would be compared against this tap's origin.
      indexAtSetRef.current = null;
      optimisticActiveTabStore.clearSilently();
      return;
    }
    const indexAtSet = indexAtSetRef.current;
    if (indexAtSet === null) {
      return;
    }
    if (realIndex !== indexAtSet || optimisticIndex === -1) {
      optimisticActiveTabStore.clear();
    }
  }, [optimisticActiveName, optimisticActiveTabStore, optimisticIndex, realIndex]);

  const isOptimisticPending = optimisticIndex !== -1 && optimisticIndex !== realIndex;
  const effectiveState = isOptimisticPending
    ? { ...props.state, index: optimisticIndex }
    : props.state;
  const activePrimaryNavigationDestinationName = getFocusedRouteName(effectiveState);
  // Only an optimistic promotion the navigator has NOT caught up to is "pending". A re-tap of
  // the focused tab converges immediately (optimisticIndex === realIndex), so it never paints
  // the loading treatment.
  const pendingPrimaryNavigationDestinationName = isOptimisticPending ? optimisticActiveName : null;

  // UI-thread mirror so Reanimated consumers can read the current active tab without
  // reading JS context in a worklet.
  const activeNameSharedValue = useSharedValue<string | null>(
    activePrimaryNavigationDestinationName,
  );
  useLayoutEffect(() => {
    activeNameSharedValue.set(activePrimaryNavigationDestinationName);
  }, [activePrimaryNavigationDestinationName, activeNameSharedValue]);

  return (
    <ActivePrimaryNavigationDestinationNameSharedValueContext.Provider
      value={activeNameSharedValue}
    >
      <ActivePrimaryNavigationDestinationNameContext.Provider
        value={activePrimaryNavigationDestinationName}
      >
        <PendingPrimaryNavigationDestinationNameContext.Provider
          value={pendingPrimaryNavigationDestinationName}
        >
          <PrimaryNavigationStrip
            {...props}
            androidUsesButtonNavBar={androidUsesButtonNavBar}
            bottomClearance={bottomClearance}
            destinationOrder={destinationOrder}
            state={effectiveState}
          />
        </PendingPrimaryNavigationDestinationNameContext.Provider>
      </ActivePrimaryNavigationDestinationNameContext.Provider>
    </ActivePrimaryNavigationDestinationNameSharedValueContext.Provider>
  );
}

/** What chrome outside the tab navigator (the onboarding's tour sheet) may ask of it. */
export type PrimaryNavigationLayoutHandle = {
  /** Switch to a tab by screen name (relative to `base`), as a tap on its strip button does. */
  switchTab: (screenName: string) => void;
};

export function PrimaryNavigationLayout({
  base,
  // Blurred tabs stay mounted but are NOT frozen. Freezing a blurred scene (the patched
  // expo-router `HanFreeze`: react-freeze's thrown never-resolving thenable — and, tried as an
  // alternative, React 19's `<Activity mode="hidden">`) reproducibly wedges the JS thread the
  // first time the Stats tab mounts while a second scene (Settings → Controls, then Learn) is
  // mounted-and-frozen: React re-processes the hidden subtree in a render+commit loop (root
  // bailouts, an Offscreen retry with a per-cycle throw, a full Fabric re-commit each cycle, JS
  // pegged at 100%) that starves every timer, so TanStack's batched notification never fires and
  // the Stats index redirect sits on its skeleton for minutes until a touch event breaks the
  // loop. Han's `useQuery` already defaults `subscribed` to navigation focus, which keeps a
  // blurred tab's data work quiet — the part freezing was buying. Hermes profile + repro steps
  // in memory `focus-gated-page-navigator-redirect-stranded-tab-entry`.
  freezeOnBlur = false,
  ref,
  screens,
  showPrimaryNavigationStrip = true,
  stripOrder,
}: {
  base: RoutePath;
  freezeOnBlur?: boolean;
  ref?: Ref<PrimaryNavigationLayoutHandle>;
  /** Registration order is fixed; the strip shows tabs in this order (see `stripOrder`). */
  screens: PrimaryNavigationLayoutScreen[];
  showPrimaryNavigationStrip?: boolean;
  /**
   * Screen names (relative to `base`) in the order their tab buttons appear. Tabs it omits follow
   * in `screens` order. Reordering never re-registers screens, so no scene moves or remounts.
   */
  stripOrder?: readonly string[];
}) {
  const navigateToTabRef = useRef<((name: string) => void) | null>(null);
  const { theme: uniwindTheme } = useUniwind();
  const { colors } = useTheme();
  const backgroundChannels = useCSSVariable("--color-background");
  const mutedChannels = useCSSVariable("--color-muted");
  const foregroundChannels = useCSSVariable("--color-foreground");
  const mutedForegroundChannels = useCSSVariable("--color-muted-foreground");
  const primaryChannels = useCSSVariable("--color-primary");
  const insets = useSafeAreaInsets();
  const { height, width } = useWindowDimensions();
  const isLandscape = Platform.OS !== "web" && width > height;
  const [androidUsesButtonNavBar] = useContextSelectors(
    NavContext,
    (c) => c.androidUsesButtonNavBar,
  );
  const isPrimaryNavigationStripHidden = useIsPrimaryNavigationStripHidden();

  const tabBarBottomClearance = getPrimaryNavigationBarBottomClearance(insets.bottom, {
    androidUsesButtonNavBar,
  });
  const isDarkMode = uniwindTheme === "dark";
  const headerTitleRegistry = useContext(PrimaryControlsStripTitleContext);

  const sceneBackgroundColor = hslFromChannels(backgroundChannels) ?? colors.background;
  const tabBarBackgroundColor = hslFromChannels(mutedChannels) ?? colors.background;
  const activeTintColor =
    hslFromChannels(isDarkMode ? foregroundChannels : primaryChannels) ?? colors.text;
  const inactiveTintColor = hslFromChannels(mutedForegroundChannels) ?? colors.text;

  /**
   * Optimistic focused-tab name. Set synchronously in the tab-press listener so the indicator
   * + active-tint update on the same frame as the tap — without waiting for the destination
   * route to finish rendering. `OptimisticPrimaryNavigationStrip` clears it once the real navigator
   * state catches up (or diverges, e.g. when the user navigates to a drawer route). An external
   * store rather than state here, so a tap never re-renders this layout (see the store).
   */
  const [optimisticActiveTabStore] = useState(createOptimisticActiveTabStore);

  /**
   * Which tab is currently held down. Drives the icon's press treatment, which starts on
   * press-IN so a tap is acknowledged even when the destination commits in the same frame.
   * An external store for the same reason as the optimistic one above: a press must not
   * re-render this layout.
   */
  const [pressedTabStore] = useState(createPressedPrimaryNavigationDestinationStore);

  const [sceneRemountStore] = useState(createPrimaryNavigationSceneRemountStore);

  const tabPressIdRef = useRef(0);
  const clearOptimisticHeaderTitleRef = useRef<(() => void) | null>(null);
  const markOptimisticActive = optimisticActiveTabStore.set;

  // The same switch an unfocused tab's press makes (see its tabPress listener below).
  useImperativeHandle(
    ref,
    () => ({
      switchTab(screenName) {
        markOptimisticActive(screenName);
        navigateToTabRef.current?.(screenName);
      },
    }),
    [markOptimisticActive],
  );

  /**
   * Name of the tab the navigator has COMMITTED focus to, mirrored from the tab bar's
   * `props.state` on every render. Exposed via `CommittedPrimaryNavigationDestinationNameRefContext` so frozen
   * subtrees (react-native-screens `freezeOnBlur`) can synchronously read the active tab
   * without waiting for a render (focus gating, DB-invalidation rejection).
   *
   * Deliberately NOT used for the tabPress re-tap decision: this ref only updates when React
   * re-renders the tab bar, so under a busy JS thread it lags every dispatch that hasn't
   * rendered yet — two taps processed in one batch would both read the pre-batch value. The
   * tabPress listener reads `navigation.getState()` instead, which React Navigation reduces
   * synchronously on every dispatch (see the listener below).
   */
  const committedFocusedNameRef = useRef<string | null>(null);
  const syncCommittedFocusedName = useCallback((name: string | null) => {
    committedFocusedNameRef.current = name;
  }, []);

  useEffect(() => {
    if (Platform.OS !== "android") {
      return;
    }
    NavigationBar.setStyle(isDarkMode ? "light" : "dark");
  }, [isDarkMode]);

  return (
    <PressedPrimaryNavigationDestinationStoreContext.Provider value={pressedTabStore}>
      <CommittedPrimaryNavigationDestinationNameRefContext.Provider value={committedFocusedNameRef}>
        {/*
        Most destinations can reveal as soon as their navigation commits. Learn startup instead
        reveals from LearnSummarySurface: its local fallback always paints a card and bottom controls
        without waiting for data. Revealing here would expose the empty redirect/scene first.
      */}
        <NavTabsChromeReady />
        {/* Spans the scenes and the strip: Settings edits the strip in place, and the dragged
            icon travels on the provider's ghost layer above both. */}
        <PrimaryNavigationCustomizationProvider>
          <PrimaryNavigationStripFadeReachProvider>
            <PrimaryNavigationStripFadeInsetContext
              value={
                isLandscape || isPrimaryNavigationStripHidden || !showPrimaryNavigationStrip
                  ? 0
                  : PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX
              }
            >
              <Tabs
                // Under the TabRouter default (`firstRoute`), a GO_BACK that no in-tab stack can handle
                // is "handled" by this navigator jumping to the FIRST tab (Stats) — i.e. a back press on
                // a tab with empty history silently teleports the user across bottom tabs. `none` makes
                // cross-tab back impossible at the navigator level: tab switches never create back
                // history, and an unhandled GO_BACK stays unhandled instead of switching tabs. All back
                // UI goes through `useScopedBack` (routes/nav/_lib/back), which never dispatches a
                // GO_BACK that would reach this navigator. Same principle as the morpheme-panels
                // MaterialTopTabs (`morpheme-panels-tabs-layout.tsx`).
                backBehavior="none"
                safeAreaInsets={{
                  bottom: Platform.OS === "ios" ? 0 : tabBarBottomClearance,
                }}
                screenLayout={({ children, route }) => (
                  <PrimaryNavigationSceneRemountBoundary
                    name={route.name}
                    sceneRemountStore={sceneRemountStore}
                  >
                    {children}
                  </PrimaryNavigationSceneRemountBoundary>
                )}
                screenOptions={{
                  // Drawer destinations are peer pages, so switching them is instantaneous.
                  animation: "none",
                  // Inactive tab subtrees stay MOUNTED across blur either way (`detachInactiveScreens`
                  // is untouched): returning to a tab never re-mounts its tree — e.g. the Cards/Levels
                  // grid would otherwise re-mount hundreds of character buttons every visit, and the
                  // Learn tab's `usePrefetchedSuspenseQuery` would re-suspend and flash a blank screen.
                  // `lazy` still defers the first mount; live-query-driven queryKeys still refetch on
                  // change. Whether the blurred tree is additionally FROZEN is `freezeOnBlur` — off by
                  // default, see the prop comment above.
                  freezeOnBlur,
                  headerShown: false,
                  lazy: true,
                  sceneStyle: {
                    backgroundColor: sceneBackgroundColor,
                  },
                  tabBarActiveTintColor: activeTintColor,
                  tabBarButton: (buttonProps) => (
                    <BalancedPrimaryNavigationStripButton {...buttonProps} />
                  ),
                  tabBarInactiveTintColor: inactiveTintColor,
                  tabBarItemStyle: {
                    // Joined navigation-strip cells keep flat edges (rounded-none).
                    borderRadius: 0,
                    marginHorizontal: 0,
                    paddingHorizontal: 0,
                  },
                  // The rail occupies the edge opposite the sensor housing.
                  tabBarPosition: isLandscape
                    ? insets.left > insets.right
                      ? "right"
                      : "left"
                    : "bottom",
                  tabBarStyle: {
                    ...(!showPrimaryNavigationStrip ? { height: 0 } : {}),
                    backgroundColor: tabBarBackgroundColor,
                    // Borderless by design: the bar reads as chrome against the scene background, and
                    // The scene's alpha mask ends directly above the icons.
                    borderTopWidth: 0,
                    elevation: 0,
                    paddingHorizontal: 0,
                    paddingTop: 0,
                    shadowOpacity: 0,
                  },
                }}
                tabBar={(props) => {
                  navigateToTabRef.current = (name) => {
                    const route = props.state.routes.find((candidate) => candidate.name === name);
                    if (route !== undefined) {
                      props.navigateToTab(route.key);
                    }
                  };
                  if (!showPrimaryNavigationStrip) {
                    syncCommittedFocusedName(getFocusedRouteName(props.state));
                    // oxlint-disable-next-line haniverse/no-return-null -- react-navigation `tabBar` render-prop contract: `null` means "no tab bar"
                    return null;
                  }
                  // A focused screen can hide the whole bar via `<SlideOptions isPrimaryNavigationStripHidden />`
                  // (e.g. the onboarding welcome slide). Rendering nothing here removes it entirely;
                  // the screen restores the flag when it unmounts.
                  return isPrimaryNavigationStripHidden ? null : (
                    <OptimisticPrimaryNavigationStrip
                      {...props}
                      androidUsesButtonNavBar={androidUsesButtonNavBar}
                      bottomClearance={tabBarBottomClearance}
                      destinationOrder={stripOrder}
                      onCommittedFocusedName={syncCommittedFocusedName}
                      optimisticActiveTabStore={optimisticActiveTabStore}
                    />
                  );
                }}
              >
                {screens.map((screen) => {
                  if (screen.tab === null) {
                    return (
                      <Tabs.Screen
                        key={screen.route}
                        name={path.relative(base, screen.route) || "index"}
                        options={hiddenOptions}
                      />
                    );
                  }

                  const screenName = path.relative(base, screen.route);
                  const tabBarLabel = screen.tab.tabBarLabel;
                  const dismissToHrefOption = screen.tab.dismissToOnPrimaryNavigationPress;
                  const onAfterDismiss = screen.tab.onAfterDismissOnPrimaryNavigationPress;
                  const preserveBaseScene = screen.tab.preserveBaseSceneOnPrimaryNavigationPress;
                  const onFocusedPress = screen.tab.onFocusedPrimaryNavigationPress;
                  const optimisticPrimaryControlsStripTitle =
                    screen.tab.optimisticPrimaryControlsStripTitle;

                  const listeners = ({
                    navigation,
                  }: {
                    navigation: {
                      getState: () => TabsState;
                    };
                  }) => ({
                    tabPress: (e: { preventDefault: () => void }) => {
                      const tabPressId = ++tabPressIdRef.current;
                      // Promote the tapped tab immediately so the indicator + active tint flip on the
                      // current frame, regardless of how slowly the destination scene renders.
                      markOptimisticActive(screenName);

                      clearOptimisticHeaderTitleRef.current?.();
                      clearOptimisticHeaderTitleRef.current =
                        optimisticPrimaryControlsStripTitle === undefined
                          ? null
                          : headerTitleRegistry.publishOptimistic(
                              optimisticPrimaryControlsStripTitle,
                            );

                      // "Re-tap or switch?" MUST be decided from `navigation.getState()`: React
                      // Navigation reduces every dispatch into its store synchronously, so this is
                      // up to date even when React hasn't re-rendered yet. Any render-lagged source
                      // (`navigation.isFocused()`, `committedFocusedNameRef`) reports the PRE-batch tab
                      // when two taps are processed in one JS batch (busy JS thread), misreading a
                      // switch as a re-tap. That misfire used to `preventDefault` the legitimate switch
                      // and fire a queued `dismissTo` against a future, different state — yanking the
                      // navigator across tabs mid-switch, wiping the destination tab's saved state, and
                      // wedging the blurred tab's frozen screen (e.g. Browse > Levels) painted on top of
                      // the focused scene while header/tab-bar showed the other tab.
                      const tabsState = navigation.getState();
                      const focusedTabRoute = tabsState.routes[tabsState.index];
                      const isAlreadyFocused = focusedTabRoute?.name === screenName;

                      if (!isAlreadyFocused) {
                        // Switch immediately. Do not pop the destination's nested stack or wait for its
                        // data: the destination page owns its loading UI, while each tab saves its internal
                        // navigation state across switches. Returning to Browse from another tab, for
                        // example, lands on the previously viewed morpheme rather than the Levels list.
                        // `freezeOnBlur` keeps blurred subtrees mounted-but-frozen, so the switch simply
                        // re-focuses the preserved tree.
                        //
                        // Dispatch the switch OURSELVES instead of relying on the tab bar's default:
                        // `BottomTabBar`'s onPress guards its dispatch with the RENDER-time `focused`
                        // flag (`index === state.index` — and `state` here is our optimistic
                        // `effectiveState`). Under a busy JS thread that render lags the store, so a
                        // legitimate switch tap (e.g. Browse → Decks → Browse before renders catch up)
                        // is silently swallowed — leaving the navigator on the wrong tab with the
                        // optimistic indicator stranded. Explicit dispatch makes the switch
                        // unconditional and atomic with the fresh-state decision above.
                        //
                        // Resetting to the tab root (`dismissToSynchronously` + `onAfterDismiss`, e.g.
                        // `requestBrowseReset`) is reserved for a tap on the ALREADY-focused tab,
                        // handled below — that's the "tap the active tab to go home" affordance.
                        e.preventDefault();
                        // SDK 58's standard tab state has no navigator key. A root JUMP_TO
                        // therefore cannot target this instance and may switch a retained
                        // app stack underneath auth. The tab-bar callback is bound to this
                        // navigator and can also activate a cold/preloaded destination.
                        navigateToTabRef.current?.(screenName);
                        return;
                      }

                      if (onFocusedPress?.() === true) {
                        e.preventDefault();
                        return;
                      }

                      if (dismissToHrefOption === undefined) {
                        // Focused tap with no dismiss target: nothing to do; the default tab-press
                        // on a focused tab is already a no-op.
                        return;
                      }

                      e.preventDefault();
                      const runAfterDismiss = () => {
                        if (onAfterDismiss === undefined) {
                          return;
                        }
                        safeRequestAnimationFrame(
                          () => {
                            if (tabPressIdRef.current !== tabPressId) {
                              return;
                            }
                            onAfterDismiss();
                          },
                          "primary-navigation-after-dismiss",
                          { frames: 2 },
                        );
                      };

                      if (
                        isOnBaseRoute(
                          focusedTabRoute?.state,
                          path
                            .relative(screen.route, dismissToHrefOption)
                            .split("/")
                            .filter(Boolean),
                        )
                      ) {
                        // Already on the base page: re-render it in place instead of dispatching the
                        // dismiss href. When the base page was reached through a redirect route, that
                        // href names a route no longer in the stack, and `POP_TO` then replaces the
                        // top route — sliding a fresh copy in from the right. A keyed remount gives
                        // the same fresh page with no transition. Scenes with an in-place action
                        // (Browse's current-level scroll) retain their list instead.
                        if (!preserveBaseScene) {
                          sceneRemountStore.bump(screenName);
                        }
                        runAfterDismiss();
                        return;
                      }

                      // Focused tap: default tab-press is a no-op, so dismiss back to the tab root
                      // ourselves. `dismissToSynchronously` — never `router.dismissTo` — so the dismiss
                      // resolves and dispatches in THIS task, atomically with the focus check above.
                      // `router.*` calls execute from a `useEffect` on a later render (expo-router's
                      // `routingQueue`), can't be cancelled, and resolve their target against whatever
                      // state exists by then — a tab switch tapped in that gap would be overridden by a
                      // stale cross-tab dismiss (the wedge described above).
                      dismissToSynchronously(dismissToHrefOption);
                      runAfterDismiss();
                    },
                  });

                  return (
                    <Tabs.Screen
                      key={screen.route}
                      listeners={listeners}
                      name={screenName}
                      options={{
                        lazy: !(base === "/(nav)/(app)" && screenName === "learn"),
                        retainNativeScene: base === "/(nav)/(app)" && screenName === "learn",
                        tabBarAccessibilityLabel: screen.tab.accessibilityLabel ?? screen.tab.title,
                        tabBarButtonTestID: `app-primary-navigation-${path.basename(screenName)}`,
                        tabBarIcon: screen.tab.icon,
                        tabBarLabel: ({ color, focused }) => (
                          <PrimaryNavigationStripLabel
                            color={color}
                            focused={focused}
                            label={tabBarLabel}
                          />
                        ),
                        title: screen.tab.title,
                      }}
                    />
                  );
                })}
              </Tabs>
            </PrimaryNavigationStripFadeInsetContext>
          </PrimaryNavigationStripFadeReachProvider>
        </PrimaryNavigationCustomizationProvider>
      </CommittedPrimaryNavigationDestinationNameRefContext.Provider>
    </PressedPrimaryNavigationDestinationStoreContext.Provider>
  );
}
